import './App.css';
import { MainNavigation } from './components/MainNavigation';
import { AllSimulations } from './components/AllSimulations';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useParams } from 'react-router-dom';
import SimulationResults from './components/SimulationResults';
import WhatChanged from './components/WhatChanged';
import SnapshotView from './components/SnapshotView';

import "normalize.css";
import "@blueprintjs/core/lib/css/blueprint.css";
import "@blueprintjs/icons/lib/css/blueprint-icons.css";
import "./brand.css";
import logo from "./assets/logo-horizontal.svg";
// import "@blueprintjs/datetime/lib/css/blueprint-datetime.css";
// import "@blueprintjs/popover2/lib/css/blueprint-popover2.css";
// import "@blueprintjs/select/lib/css/blueprint-select.css";
import { useAuth } from 'react-oidc-context';
import { useEffect } from 'react';
import { getAllTeamSimulations, getAllUserSimulations, getSignalSnapshot, getSimulationResults, getUsers, getWhatChangedComparison } from './api/cppmodelApi';
import { UserAdministration } from './components/UserAdministration';
import { Button } from '@blueprintjs/core';
import { claimSignInRetry, signInAndReturn } from './auth/signIn';

const HomePage: React.FC = () => {
  return <div>
    {/* Navigation Bar */}
    <MainNavigation />
    <main>
      {/* {navigation.state === 'loading' && <p>Loading...</p>} */}
      <Outlet />
    </main>
  </div>
};
// Links the SDK prints after submitting a run ("UI: https://<host>/simulations/<name>")
// keep working: they open the results page of that simulation (or execution).
const LegacySimulationLink: React.FC = () => {
  const { simulationId = '', executionId } = useParams();
  const path = `/results/${encodeURIComponent(simulationId)}${executionId ? `/executions/${encodeURIComponent(executionId)}` : ''}`;

  return <Navigate to={path} replace />;
};

// Created on first use, after the sign-in callback has restored the original URL
let router: ReturnType<typeof createBrowserRouter> | undefined;
const getRouter = () => router ??= createBrowserRouter([
  {
    path: '/',
    element: <HomePage />,
    id: 'root',
    children: [
      {
        index: true,
        // path: '/user-simulations',
        element: <AllSimulations scope='user' />,
        loader: getAllUserSimulations
      },
      {
        path: "/team-simulations",
        element: <AllSimulations scope='team' />,
        loader: getAllTeamSimulations

      },
      {
        path: "/results/:simulationId",
        element: <SimulationResults />,
        loader: getSimulationResults
      },
      {
        path: "/results/:simulationId/executions/:executionId",
        element: <SimulationResults />,
        loader: getSimulationResults
      },
      {
        path: "/simulations/:simulationId",
        element: <LegacySimulationLink />
      },
      {
        path: "/simulations/:simulationId/executions/:executionId",
        element: <LegacySimulationLink />
      },
      {
        path: "/what-changed/:simulationId",
        element: <WhatChanged />,
        loader: getWhatChangedComparison
      },
      {
        path: "/snapshot/:snapshotId",
        element: <SnapshotView />,
        loader: getSignalSnapshot
      },
      {
        path: "/snapshots/:snapshotId",
        element: <SnapshotView />,
        loader: getSignalSnapshot
      },
      {
        path: "/users",
        element: <UserAdministration />,
        loader: getUsers
      }
    ]
  }]);

const Splash: React.FC<{ message: string; action?: React.ReactNode }> = ({ message, action }) => (
  <div className="cm-splash">
    <div className="cm-splash-inner">
      <img src={logo} alt="CppModel" />
      <span>{message}</span>
      {action}
    </div>
  </div>
);

const App: React.FC = () => {
  const auth = useAuth();

  // const getHostname = (auth: AuthContextProps): string => {
  //   const user = auth.user?.profile as { groups?: string[] };
  //   const groups = user?.groups as string[];
  //   return `${groups[0]}.cppmodel.com`;
  // }

  useEffect(() => {
    // A failed sign-in (e.g. its response finished in another tab) is retried
    // once a minute at most; otherwise its error is shown.
    if (!auth.isAuthenticated && !auth.isLoading && (!auth.error || claimSignInRetry())) {
      // Redirect to login if not authenticated, remembering where to come back to
      signInAndReturn(auth);
    // } else if (auth.isAuthenticated) {
    //   const defaultHost = getHostname(auth);
    //   const currentHost = window.location.hostname;

    //   if (currentHost !== defaultHost && currentHost !== "localhost") {
    //     window.location.href = 'https://' + defaultHost;
    //     // } else {
    //     //   console.error("No groups found in the token.");
    //     //   // Handle cases where no groups are available
    //   }
    }
  }, [auth.isAuthenticated, auth.isLoading, auth]);
  if (auth.isLoading) return <Splash message="Loading..." />; // Optional loading message

  if (auth.error) {
    return (
      <Splash
        message={`Oops... ${auth.error.message}`}
        action={<Button intent="primary" onClick={() => signInAndReturn(auth)}>Sign in again</Button>}
      />
    );
  }

  // Render the protected content only if authenticated
  return auth.isAuthenticated ? (
    <RouterProvider router={getRouter()} />
  ) : null;
};

export default App;
